/**
 * Authentication & Authorization Types
 *
 * Raptor Predator Frontend - Security Types
 *
 * Defines the RBAC (Role-Based Access Control) system types:
 * - User roles and permissions
 * - Authentication state
 * - Audit logging types
 *
 * @security SEC-001: Role-Based Access Control Implementation
 * @priority P0 CRITICAL
 *
 * Peer Review Completed:
 * - Gemini 3 Pro: 7/10 (architectural concerns addressed)
 * - GPT-4o: 8/10 (token security recommendations noted)
 *
 * SECURITY NOTICE - CRITICAL:
 * ============================
 * Frontend RBAC is ADVISORY ONLY. All permission checks MUST be enforced
 * server-side. The backend API MUST:
 * 1. Validate JWT tokens on every request
 * 2. Check user permissions before executing any action
 * 3. Return 403 Forbidden for unauthorized requests
 * 4. Log all 403 responses for compliance (server-side audit trail)
 *
 * Frontend permission checks are for UX purposes only - they prevent
 * users from seeing actions they can't perform, but do NOT provide
 * actual security. An attacker can bypass all frontend checks.
 *
 * Token Storage Security:
 * - Access tokens: Short-lived (15-60 min), stored in memory only
 * - Refresh tokens: Should be HttpOnly cookies (not accessible to JS)
 * - NEVER store refresh tokens in localStorage (XSS vulnerability)
 */

// ============================================================================
// Role Definitions
// ============================================================================

/**
 * User roles in the system, ordered by privilege level
 * Higher roles inherit all permissions of lower roles
 * 
 * Role Mapping (unified with hooks/useAuth.ts):
 * - viewer: Read-only access
 * - operator: Basic operational actions
 * - analyst: Security analysis (alias for legacy compatibility)
 * - security_analyst: Security operations
 * - senior_analyst: Advanced security + some admin
 * - admin: Full admin except destructive
 * - super_admin: Full access including destructive
 */
export type UserRole = 
  | 'viewer'
  | 'operator'
  | 'analyst'
  | 'security_analyst'
  | 'senior_analyst'
  | 'admin'
  | 'super_admin';

/**
 * Role hierarchy - maps each role to its level (higher = more privilege)
 */
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  viewer: 0,
  operator: 1,
  analyst: 2,
  security_analyst: 2,
  senior_analyst: 3,
  admin: 4,
  super_admin: 5,
} as const;

/**
 * Human-readable role names for UI display
 */
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  viewer: 'Viewer',
  operator: 'Operator',
  analyst: 'Analyst',
  security_analyst: 'Security Analyst',
  senior_analyst: 'Senior Analyst',
  admin: 'Administrator',
  super_admin: 'Super Administrator',
} as const;

// ============================================================================
// Permission Definitions
// ============================================================================

/**
 * Permission categories for granular access control
 * Includes both singular (API) and plural (legacy) forms for compatibility
 */
export type PermissionCategory =
  | 'dashboard'
  | 'threat'
  | 'threats'
  | 'policy'
  | 'policies'
  | 'firewall'
  | 'remediation'
  | 'inoculation'
  | 'network'
  | 'admin'
  | 'expert'
  | 'telemetry'
  | 'audit'
  | 'export';

/**
 * Action types that can be gated by permissions
 */
export type PermissionAction =
  | 'view'
  | 'create'
  | 'edit'
  | 'delete'
  | 'execute'
  | 'approve'
  | 'rollback'
  | 'export'
  | 'raw_view'
  | 'deploy'
  | 'hunt'
  | 'isolate'
  | 'resolve'
  | 'eliminate'
  | 'skip_preflight'
  | 'record_workflow'
  | 'deploy_workflow'
  | 'users'
  | 'roles'
  | 'settings'
  | 'backup';

/**
 * Permission string format: category:action
 * Examples: 'threats:view', 'remediation:execute', 'inoculation:approve'
 */
export type Permission = `${PermissionCategory}:${PermissionAction}`;

/**
 * Permission matrix - defines which roles have which permissions
 * Note: Higher roles inherit all permissions from lower roles
 *
 * IMPORTANT: This is the DEFAULT matrix for UI rendering. In production,
 * fetch user permissions from the backend on login (GET /api/auth/permissions)
 * to ensure frontend and backend stay in sync. Backend is source of truth.
 */
export const PERMISSION_MATRIX: Record<UserRole, Permission[]> = {
  viewer: [
    'dashboard:view',
    'threats:view',
    'policies:view',
    'network:view',
    'telemetry:view',
  ],
  operator: [
    // Basic operational access
    'dashboard:view',
    'threats:view',
    'policies:view',
    'network:view',
    'telemetry:view',
    'remediation:view',
  ],
  analyst: [
    // Inherits from viewer
    'dashboard:view',
    'threats:view',
    'policies:view',
    'network:view',
    'telemetry:view',
    // Additional permissions
    'threats:create',
    'threats:edit',
    'remediation:view',
    'inoculation:view',
    'expert:view',
    'audit:view',
    'export:view',
  ],
  security_analyst: [
    // Same as analyst
    'dashboard:view',
    'threats:view',
    'policies:view',
    'network:view',
    'telemetry:view',
    'threats:create',
    'threats:edit',
    'remediation:view',
    'inoculation:view',
    'expert:view',
    'audit:view',
    'export:view',
    // Additional security-specific permissions
    'remediation:execute',
    'expert:execute',
  ],
  senior_analyst: [
    // Inherits from analyst
    'dashboard:view',
    'threats:view',
    'threats:create',
    'threats:edit',
    'policies:view',
    'network:view',
    'telemetry:view',
    'remediation:view',
    'inoculation:view',
    'expert:view',
    'audit:view',
    'export:view',
    // Additional permissions
    'policies:create',
    'policies:edit',
    'remediation:execute',
    'remediation:create',
    'inoculation:create',
    'expert:execute',
    'export:export',
  ],
  admin: [
    // Full access - all permissions
    'dashboard:view',
    'threats:view',
    'threats:create',
    'threats:edit',
    'threats:delete',
    'policies:view',
    'policies:create',
    'policies:edit',
    'policies:delete',
    'network:view',
    'network:edit',
    'telemetry:view',
    'telemetry:raw_view',
    'remediation:view',
    'remediation:create',
    'remediation:execute',
    'remediation:rollback',
    'inoculation:view',
    'inoculation:create',
    'inoculation:execute',
    'inoculation:approve',
    'inoculation:rollback',
    'expert:view',
    'expert:execute',
    'audit:view',
    'audit:export',
    'export:view',
    'export:export',
    'admin:view',
    'admin:edit',
  ],
  super_admin: [
    // Full access including destructive operations
    'dashboard:view',
    'threats:view',
    'threats:create',
    'threats:edit',
    'threats:delete',
    'policies:view',
    'policies:create',
    'policies:edit',
    'policies:delete',
    'network:view',
    'network:edit',
    'telemetry:view',
    'telemetry:raw_view',
    'remediation:view',
    'remediation:create',
    'remediation:execute',
    'remediation:rollback',
    'inoculation:view',
    'inoculation:create',
    'inoculation:execute',
    'inoculation:approve',
    'inoculation:rollback',
    'expert:view',
    'expert:execute',
    'audit:view',
    'audit:export',
    'export:view',
    'export:export',
    'admin:view',
    'admin:edit',
    'admin:delete',
  ],
} as const;

// ============================================================================
// User Types
// ============================================================================

/**
 * User representation in the system
 */
export interface User {
  /** Unique user identifier */
  id: string;
  /** User's display name */
  name: string;
  /** User's email address */
  email: string;
  /** User's assigned role */
  role: UserRole;
  /** Optional avatar URL */
  avatarUrl?: string;
  /** Optional department/team */
  department?: string;
  /** When the user last logged in */
  lastLoginAt?: Date;
  /** When the user was created */
  createdAt: Date;
  /** Whether MFA is enabled */
  mfaEnabled: boolean;
  /** Session expiry time */
  sessionExpiresAt?: Date;
  /** Number of successful logins (tracked for security) */
  loginCount?: number;
}

/**
 * Authentication token structure
 */
export interface AuthToken {
  /** JWT access token */
  accessToken: string;
  /** Refresh token for token renewal */
  refreshToken: string;
  /** Token type (usually 'Bearer') */
  tokenType: string;
  /** Seconds until token expiration */
  expiresIn: number;
  /** When the token was issued */
  issuedAt: Date;
}

/**
 * Authentication state
 */
export type AuthStatus =
  | 'idle'
  | 'loading'
  | 'authenticated'
  | 'unauthenticated'
  | 'mfa_required'
  | 'session_expired'
  | 'awaiting_passcode';

/**
 * Login credentials
 */
export interface LoginCredentials {
  email: string;
  password: string;
  mfaCode?: string;
  rememberMe?: boolean;
}

/**
 * Authentication error types
 */
export type AuthErrorCode =
  | 'invalid_credentials'
  | 'account_locked'
  | 'mfa_required'
  | 'mfa_invalid'
  | 'session_expired'
  | 'token_invalid'
  | 'permission_denied'
  | 'network_error'
  | 'unknown'
  | 'user_not_found'
  | 'passcode_request_failed'
  | 'invalid_passcode'
  | 'verification_failed'
  | 'validation_failed'; // User data validation failed (runtime check)

/**
 * Authentication error
 */
export interface AuthError {
  code: AuthErrorCode;
  message: string;
  details?: Record<string, unknown>;
}

// ============================================================================
// Audit Types
// ============================================================================

/**
 * Audit log entry for security tracking
 *
 * IMPORTANT: Client-side audit logs are for TELEMETRY and UX ANALYTICS only.
 * They are NOT suitable for compliance or security investigations because:
 * 1. Users can block network requests that send logs
 * 2. Users can clear browser state/close browser
 * 3. Users can manipulate client-side state
 *
 * For compliance: Backend API must log all 403 responses server-side.
 */
export interface AuditLogEntry {
  /** Unique audit entry ID */
  id: string;
  /** User who performed the action */
  userId: string;
  /** User's role at time of action */
  userRole: UserRole;
  /** Permission that was checked */
  permission: Permission;
  /** Whether the action was allowed */
  allowed: boolean;
  /** The action attempted */
  action: string;
  /** Resource type being accessed */
  resourceType: string;
  /** Resource ID being accessed */
  resourceId?: string | undefined;
  /** IP address of the request */
  ipAddress: string;
  /** User agent string */
  userAgent: string;
  /** When the action occurred */
  timestamp: Date;
  /** Additional context/metadata */
  metadata?: Record<string, unknown> | undefined;
  /** Session ID for correlation */
  sessionId: string;
}

/**
 * Audit query filters
 */
export interface AuditQueryFilters {
  userId?: string;
  userRole?: UserRole;
  permission?: Permission;
  allowed?: boolean;
  resourceType?: string;
  startDate?: Date;
  endDate?: Date;
  page?: number;
  pageSize?: number;
}

// ============================================================================
// Permission Checker Types
// ============================================================================

/**
 * Permission check context
 */
export interface PermissionCheckContext {
  /** User requesting permission */
  user: User;
  /** Permission being checked */
  permission: Permission;
  /** Optional resource being accessed */
  resourceId?: string;
  /** Optional resource owner */
  resourceOwnerId?: string;
  /** Additional context */
  context?: Record<string, unknown>;
}

/**
 * Permission check result
 */
export interface PermissionCheckResult {
  /** Whether permission is granted */
  allowed: boolean;
  /** Reason for the decision */
  reason: string;
  /** Required role for this permission */
  requiredRole?: UserRole | undefined;
  /** User's current role */
  userRole?: UserRole | undefined;
  /** Permission that was checked */
  permission: Permission;
}

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Props for ProtectedRoute component
 */
export interface ProtectedRouteProps {
  /** Required permissions to access the route */
  requiredPermissions?: Permission[] | undefined;
  /** Required minimum role */
  requiredRole?: UserRole | undefined;
  /** Whether all permissions are required (AND) or any (OR) */
  requireAll?: boolean | undefined;
  /** Custom redirect path on denial */
  redirectTo?: string | undefined;
  /** Children to render if authorized */
  children: React.ReactNode;
  /** Optional fallback component */
  fallback?: React.ReactNode | undefined;
}

/**
 * Props for RoleGate component
 */
export interface RoleGateProps {
  /** Required permissions to show children */
  requiredPermissions?: Permission[] | undefined;
  /** Required minimum role */
  requiredRole?: UserRole | undefined;
  /** Whether all permissions are required (AND) or any (OR) */
  requireAll?: boolean | undefined;
  /** Children to render if authorized */
  children: React.ReactNode;
  /** What to render if unauthorized */
  fallback?: React.ReactNode | undefined;
  /** Whether to show a disabled version instead of hiding */
  showDisabled?: boolean | undefined;
  /** Tooltip to show on hover when disabled */
  disabledTooltip?: string | undefined;
}

/**
 * Props for PermissionDenied component
 */
export interface PermissionDeniedProps {
  /** Title to display */
  title?: string | undefined;
  /** Message to display */
  message?: string | undefined;
  /** Required permissions that were missing */
  requiredPermissions?: Permission[] | undefined;
  /** Required role that was missing */
  requiredRole?: UserRole | undefined;
  /** User's current role */
  currentRole?: UserRole | undefined;
  /** Whether to show a back button */
  showBackButton?: boolean | undefined;
  /** Whether to show a home button */
  showHomeButton?: boolean | undefined;
  /** Custom action component */
  customAction?: React.ReactNode | undefined;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Check if a role has at least the required privilege level
 */
export function hasMinimumRole(userRole: UserRole, requiredRole: UserRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

/**
 * Get all permissions for a role (including inherited)
 */
export function getPermissionsForRole(role: UserRole): Permission[] {
  return [...PERMISSION_MATRIX[role]];
}

/**
 * Check if a permission is included in a list
 */
export function hasPermission(
  userPermissions: Permission[],
  requiredPermission: Permission
): boolean {
  return userPermissions.includes(requiredPermission);
}

/**
 * Check if all required permissions are present
 */
export function hasAllPermissions(
  userPermissions: Permission[],
  requiredPermissions: Permission[]
): boolean {
  return requiredPermissions.every((p) => userPermissions.includes(p));
}

/**
 * Check if any of the required permissions are present
 */
export function hasAnyPermission(
  userPermissions: Permission[],
  requiredPermissions: Permission[]
): boolean {
  return requiredPermissions.some((p) => userPermissions.includes(p));
}

/**
 * Get user-friendly permission description
 */
export function getPermissionDescription(permission: Permission): string {
  const [category, action] = permission.split(':') as [PermissionCategory, PermissionAction];
  const categoryNames: Record<PermissionCategory, string> = {
    dashboard: 'Dashboard',
    threat: 'Threat',
    threats: 'Threats',
    policy: 'Policy',
    policies: 'Policies',
    firewall: 'Firewall',
    remediation: 'Remediation',
    inoculation: 'Inoculation',
    network: 'Network',
    admin: 'Administration',
    expert: 'Expert Tools',
    telemetry: 'Telemetry',
    audit: 'Audit Logs',
    export: 'Export',
  };
  const actionNames: Record<PermissionAction, string> = {
    view: 'View',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    execute: 'Execute',
    approve: 'Approve',
    rollback: 'Rollback',
    export: 'Export',
    raw_view: 'View Raw Data',
    deploy: 'Deploy',
    hunt: 'Hunt',
    isolate: 'Isolate',
    resolve: 'Resolve',
    eliminate: 'Eliminate',
    skip_preflight: 'Skip Preflight',
    record_workflow: 'Record Workflow',
    deploy_workflow: 'Deploy Workflow',
    users: 'Manage Users',
    roles: 'Manage Roles',
    settings: 'Manage Settings',
    backup: 'Backup',
  };
  return `${actionNames[action] || action} ${categoryNames[category] || category}`;
}

/**
 * Export permission requirement for a specific operation
 */
export const OPERATION_PERMISSIONS: Record<string, Permission[]> = {
  // Inoculation operations
  'inoculation:view_vectors': ['inoculation:view'],
  'inoculation:start_job': ['inoculation:execute'],
  'inoculation:approve_job': ['inoculation:approve'],
  'inoculation:rollback_job': ['inoculation:rollback'],
  'inoculation:export_report': ['inoculation:view', 'export:export'],

  // Remediation operations
  'remediation:view_jobs': ['remediation:view'],
  'remediation:execute_job': ['remediation:execute'],
  'remediation:rollback_job': ['remediation:rollback'],
  'remediation:schedule_job': ['remediation:create'],

  // Expert operations
  'expert:nl_command': ['expert:execute'],
  'expert:record_workflow': ['expert:execute'],
  'expert:execute_workflow': ['expert:execute'],

  // Policy operations
  'policy:view': ['policies:view'],
  'policy:create': ['policies:create'],
  'policy:edit': ['policies:edit'],
  'policy:delete': ['policies:delete'],

  // Telemetry operations
  'telemetry:view': ['telemetry:view'],
  'telemetry:raw_view': ['telemetry:raw_view'],

  // Export operations
  'export:csv': ['export:export'],
  'export:json': ['export:export'],
  'export:audit_logs': ['audit:export'],
} as const;

// ============================================================================
// Type Aliases for Compatibility
// ============================================================================

/**
 * Role is an alias for UserRole for backward compatibility
 * Allows importing as `Role` or `UserRole` interchangeably
 */
export type Role = UserRole;

